import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { configurationService, BrandingSettings } from '../services/configurationService';
import { authService } from '../services/authService';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
  refreshBranding: () => void;
  updateBrandingSettings: (settings: BrandingSettings) => void;
  brandingSettings: BrandingSettings | null;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

const BRANDING_CACHE_KEY = 'a3-branding-settings';

/** Default branding for PlatformAdmin when not viewing a specific tenant. */
const PLATFORM_ADMIN_BRANDING: BrandingSettings = {
  primaryColor: '#1a237e',
  secondaryColor: '#1565c0',
  logoUrl: '/logo.svg',
  faviconUrl: '/favicon.svg',
  companyName: 'GovIQ',
  applicationTitle: 'GovIQ Platform Admin',
};

function loadCachedBranding(): BrandingSettings | null {
  try {
    const raw = localStorage.getItem(BRANDING_CACHE_KEY);
    return raw ? (JSON.parse(raw) as BrandingSettings) : null;
  } catch {
    return null;
  }
}

function saveBrandingCache(settings: BrandingSettings): void {
  try {
    localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(settings));
  } catch {
    // localStorage quota exceeded — not critical
  }
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme-mode');
    return saved ? JSON.parse(saved) : false;
  });

  // Seed from cache so the correct title/logo render on first paint, no flash.
  const [brandingSettings, setBrandingSettings] = useState<BrandingSettings | null>(
    () => loadCachedBranding()
  );

  useEffect(() => {
    localStorage.setItem('theme-mode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  useEffect(() => {
    initBranding();
  }, []);

  const initBranding = async () => {
    // Determine user role to decide whether to fetch tenant branding.
    // PlatformAdmin without a tenant context should see GovIQ defaults.
    try {
      const user = await authService.getUserAsync();
      if (user?.role === 'PlatformAdmin') {
        const tenantParam = new URLSearchParams(window.location.search).get('tenant');
        if (!tenantParam) {
          // No tenant context — use platform defaults, don't fetch tenant branding
          setBrandingSettings(PLATFORM_ADMIN_BRANDING);
          saveBrandingCache(PLATFORM_ADMIN_BRANDING);
          return;
        }
      }
    } catch {
      // Auth not ready yet (e.g. first load before login) — fall through to normal fetch
    }
    await loadBrandingSettings();
  };

  const loadBrandingSettings = async () => {
    try {
      const settings = await configurationService.getBrandingSettings();
      setBrandingSettings(settings);
      saveBrandingCache(settings);
    } catch (_error) {
      // Only fall back to hardcoded defaults when there is nothing cached.
      if (!loadCachedBranding()) {
        setBrandingSettings({
          primaryColor: '#1a237e',
          secondaryColor: '#1565c0',
          logoUrl: '/logo.svg',
          faviconUrl: '/favicon.svg',
          companyName: 'GovIQ',
          applicationTitle: 'Your City Emergency Communications',
        });
      }
    }
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const refreshBranding = () => {
    // When refreshing, re-run the full role-aware init so PlatformAdmin
    // picks up tenant branding after X-Tenant-Id header is set by SettingsPage.
    initBranding();
  };

  const updateBrandingSettings = (settings: BrandingSettings) => {
    setBrandingSettings(settings);
    saveBrandingCache(settings);
  };

  // Update favicon and title when branding settings change
  useEffect(() => {
    if (brandingSettings?.faviconUrl) {
      const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (favicon) {
        favicon.href = brandingSettings.faviconUrl;
      }
    }

    if (brandingSettings?.applicationTitle) {
      document.title = brandingSettings.applicationTitle;
    }
  }, [brandingSettings?.faviconUrl, brandingSettings?.applicationTitle]);

  // WCAG AA 2.1 compliant theme configuration — aligned with IDP design system
  const theme = useMemo(() => {
    return createTheme({
      palette: {
        mode: isDarkMode ? 'dark' : 'light',
        primary: {
          main: brandingSettings?.primaryColor || (isDarkMode ? '#93C5FD' : '#1a237e'),
          contrastText: '#FFFFFF',
        },
        secondary: {
          main: brandingSettings?.secondaryColor || (isDarkMode ? '#6EE7B7' : '#1565c0'),
          contrastText: '#FFFFFF',
        },
        success: {
          main: '#2E7D32',
          light: '#4CAF50',
          dark: '#1B5E20',
          contrastText: '#FFFFFF',
        },
        warning: {
          main: '#C2710C',
          light: '#D97706',
          dark: '#92400E',
          contrastText: '#FFFFFF',
        },
        error: {
          main: '#C62828',
          light: '#EF5350',
          dark: '#7F1D1D',
          contrastText: '#FFFFFF',
        },
        info: {
          main: '#0369A1',
          light: '#0EA5E9',
          dark: '#0C4A6E',
          contrastText: '#FFFFFF',
        },
        background: {
          default: isDarkMode ? '#0F172A' : '#F8F9FA',
          paper: isDarkMode ? '#1E293B' : '#FFFFFF',
        },
        text: {
          primary: isDarkMode ? '#F1F5F9' : 'rgba(0, 0, 0, 0.87)',
          secondary: isDarkMode ? '#94A3B8' : 'rgba(0, 0, 0, 0.6)',
        },
      },
      typography: {
        fontFamily: '"Calibri", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.2 },
        h2: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.3 },
        body1: { fontSize: '1rem', lineHeight: 1.5 },
        button: { textTransform: 'none', fontWeight: 500 },
      },
      shape: { borderRadius: 4 },
      components: {
        MuiButtonBase: {
          styleOverrides: {
            root: ({ theme }) => ({
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: '3px',
                borderRadius: '2px',
              },
            }),
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              minHeight: '44px',
              borderRadius: '4px',
              fontWeight: 500,
              padding: '6px 16px',
            },
            outlined: {
              borderWidth: 1,
              '&:hover': { borderWidth: 1 },
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: { minWidth: '44px', minHeight: '44px' },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              '& .MuiOutlinedInput-root': { minHeight: '44px', borderRadius: '4px' },
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: '8px',
              boxShadow: '0px 1px 3px rgba(0,0,0,0.08), 0px 1px 2px rgba(0,0,0,0.06)',
              border: '1px solid',
              borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : '#E0E0E0',
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: { borderRadius: '4px' },
          },
        },
        MuiTableContainer: {
          styleOverrides: {
            root: {
              border: '1px solid',
              borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : '#E0E0E0',
            },
          },
        },
        MuiTableHead: {
          styleOverrides: {
            root: {
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#F5F5F5',
              '& .MuiTableCell-head': {
                fontWeight: 600,
                fontSize: '0.875rem',
                borderBottom: `2px solid ${isDarkMode ? 'rgba(255,255,255,0.12)' : '#E0E0E0'}`,
              },
            },
          },
        },
        MuiTableBody: {
          styleOverrides: {
            root: {
              '& .MuiTableRow-root': {
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#F8F9FA',
                },
              },
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              fontSize: '0.875rem',
              padding: '8px 16px',
              borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : '#E0E0E0'}`,
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: { borderRadius: '4px' },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: { borderRadius: '8px' },
          },
        },
      },
    });
  }, [isDarkMode, brandingSettings]);

  return (
    <ThemeContext.Provider
      value={{ isDarkMode, toggleTheme, refreshBranding, updateBrandingSettings, brandingSettings }}
    >
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
